import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { canAccessPage } from "@/utils/permissions";

/**
 * Check if user has any of the required roles
 */
function checkRoles(user, requiredRoles) {
  if (!user) return false;

  // App owner has all access
  if (user.isOwner) return true;

  // Check for appOwner requirement
  if (requiredRoles.includes("appOwner")) {
    return user.isOwner === true;
  }

  // Check team role
  // Default to 'owner' if user has org but no teamRole (they created the org)
  const hasOrg = user.organisationId || user.organisation;
  const teamRole = user.teamRole || (hasOrg ? "owner" : "member");
  return requiredRoles.includes(teamRole);
}

/**
 * RoleGuard - Protects routes based on user role
 *
 * Usage:
 * <Route path="/billing" element={
 *   <RoleGuard requiredRoles={['owner']}>
 *     <Billing />
 *   </RoleGuard>
 * } />
 *
 * Or with path-based checking:
 * <Route path="/billing" element={
 *   <RoleGuard>
 *     <Billing />
 *   </RoleGuard>
 * } />
 */
export function RoleGuard({
  children,
  requiredRoles = null,
  fallback = "/dashboard",
}) {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check access based on path or explicit roles
  const hasAccess = requiredRoles
    ? checkRoles(user, requiredRoles)
    : canAccessPage(user, location.pathname);

  if (!hasAccess) {
    return <Navigate to={fallback} replace />;
  }

  return children;
}

/**
 * AdminGuard - Only allows app owner (OWNER_EMAIL)
 */
export function AdminGuard({ children }) {
  return (
    <RoleGuard requiredRoles={["appOwner"]} fallback="/dashboard">
      {children}
    </RoleGuard>
  );
}

/**
 * OwnerGuard - Only allows organisation owners
 */
export function OwnerGuard({ children }) {
  return (
    <RoleGuard requiredRoles={["owner"]} fallback="/dashboard">
      {children}
    </RoleGuard>
  );
}

/**
 * AdminOrOwnerGuard - Allows organisation admins and owners
 */
export function AdminOrOwnerGuard({ children }) {
  return (
    <RoleGuard requiredRoles={["admin", "owner"]} fallback="/dashboard">
      {children}
    </RoleGuard>
  );
}

/**
 * OwnerFeatureGuard - Only allows organisation owners with specific feature access
 *
 * Usage:
 * <Route path="/settings/certificates" element={
 *   <OwnerFeatureGuard feature="digitalSignatures">
 *     <CertificatesSettings />
 *   </OwnerFeatureGuard>
 * } />
 */
export function OwnerFeatureGuard({
  children,
  feature,
  fallback = "/dashboard",
  upgradeFallback = "/settings/billing",
}) {
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasFeature, setHasFeature] = useState(false);

  useEffect(() => {
    const checkFeatureAccess = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      // App owner has all features
      if (user?.isOwner) {
        setHasFeature(true);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get("/features/me");
        if (response.data?.success) {
          const features = response.data.data?.features || {};
          // Check if the specific feature is enabled
          setHasFeature(
            features[feature]?.enabled === true || features[feature] === true,
          );
        } else {
          setHasFeature(false);
        }
      } catch (err) {
        console.error("Failed to check feature:", err);
        setHasFeature(false);
      } finally {
        setLoading(false);
      }
    };

    checkFeatureAccess();
  }, [isAuthenticated, feature, user?.isOwner]);

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for features to load
  if (loading) {
    return null;
  }

  // Check if user is org owner (or app owner)
  const hasOrg = user.organisationId || user.organisation;
  const teamRole = user.teamRole || (hasOrg ? "owner" : "member");
  const isOrgOwner = user.isOwner || teamRole === "owner";

  if (!isOrgOwner) {
    return <Navigate to={fallback} replace />;
  }

  // Check feature access
  if (!hasFeature) {
    return <Navigate to={upgradeFallback} replace />;
  }

  return children;
}

export default RoleGuard;
